import { fg } from '@confluence/feature-gating';
import { queryAgg } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import type { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';

import useStudioPermissionsLinkedWorkspaceQueryNode, {
	type useStudioPermissionsLinkedWorkspaceQuery$data,
	type useStudioPermissionsLinkedWorkspaceQuery$variables,
} from './hooks/__generated__/useStudioPermissionsLinkedWorkspaceQuery.graphql.ts';
import useStudioPermissionsWorkspaceCapabilitiesQueryNode, {
	type useStudioPermissionsWorkspaceCapabilitiesQuery$data,
	type useStudioPermissionsWorkspaceCapabilitiesQuery$variables,
} from './hooks/__generated__/useStudioPermissionsWorkspaceCapabilitiesQuery.graphql.ts';
import {
	getLinkedStudioWorkspaceAri,
	getStudioWorkspaceLookupSiteAri,
	hasStudioPermissionsBaseEligibility,
} from './utils/studioPermissions.ts';

type PreloadStudioPermissionsOptions = {
	cloudId: string | null | undefined;
	isRovoLLMEnabled: boolean | null | undefined;
	accessStatus: AccessStatus | null | undefined;
	isLoggedIn: boolean | null | undefined;
};
type QueryResult<TData> = Omit<Awaited<ReturnType<typeof queryAgg>>, 'data'> & {
	data: TData | null;
};

/**
 * Warms the store for `useStudioPermissions` so `canCreateStudioAutomation` is known during SSR.
 * Only the `cc_automations_studio_workspace_capabilities` path is preloadable; the legacy
 * `studioContext` path resolves on the client.
 */
export const preloadStudioPermissions = async ({
	cloudId,
	isRovoLLMEnabled,
	accessStatus,
	isLoggedIn,
}: PreloadStudioPermissionsOptions): Promise<
	| void
	| [
			QueryResult<useStudioPermissionsLinkedWorkspaceQuery$data>,
			QueryResult<useStudioPermissionsWorkspaceCapabilitiesQuery$data>?,
	  ]
> => {
	if (
		!hasStudioPermissionsBaseEligibility({ cloudId, isRovoLLMEnabled, accessStatus, isLoggedIn }) ||
		!fg('cc_automations_studio_workspace_capabilities')
	) {
		return;
	}

	const siteAri = getStudioWorkspaceLookupSiteAri(cloudId);
	if (!siteAri) {
		return;
	}

	const workspaceResult = await queryAgg<
		useStudioPermissionsLinkedWorkspaceQuery$data,
		useStudioPermissionsLinkedWorkspaceQuery$variables
	>({
		query: useStudioPermissionsLinkedWorkspaceQueryNode,
		variables: { siteAri },
		relayQueryPreloadRetention: 'environment',
	});

	const studioWorkspaceAri = getLinkedStudioWorkspaceAri(workspaceResult.data);
	if (!studioWorkspaceAri) {
		return [workspaceResult];
	}

	const capabilitiesResult = await queryAgg<
		useStudioPermissionsWorkspaceCapabilitiesQuery$data,
		useStudioPermissionsWorkspaceCapabilitiesQuery$variables
	>({
		query: useStudioPermissionsWorkspaceCapabilitiesQueryNode,
		variables: { workspaceId: studioWorkspaceAri.toString() },
		relayQueryPreloadRetention: 'environment',
	});

	return [workspaceResult, capabilitiesResult];
};
